import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  #breakpoint = window.matchMedia('(max-width: 989px)');

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerdown', this.pause);
    this.addEventListener('focusin', this.pause);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#breakpoint.addEventListener('change', this.#sync);
    this.#reducedMotion.addEventListener('change', this.#sync);

    this.#sync();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || !this.#isCarousel || this.#reducedMotion.matches || (this.refs.slides ?? []).length < 2) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused || !this.#isCarousel || this.#reducedMotion.matches) return;

    this.suspend();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const slides = this.refs.slides ?? [];
    if (slides.length === 0) return;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  get #isCarousel() {
    return this.#breakpoint.matches;
  }

  #sync = () => {
    this.suspend();

    if (!this.#isCarousel) {
      this.refs.slides?.forEach((slide) => slide.setAttribute('aria-hidden', 'false'));
      return;
    }

    this.current = this.#current;
    if (!this.paused && !this.#reducedMotion.matches) this.play();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
    } else if (!this.paused) {
      this.#sync();
    }
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
